/* eslint-disable max-len */
/* eslint-disable class-methods-use-this */
/* eslint-disable no-param-reassign */

import * as dateFns from "date-fns";
import * as _ from "lodash";

import {
  ArchiveType,
  ClientType,
  ConcreteBatchingPlantType,
  ConcreteMixerType,
  ConcreteRecipeType,
  ConcreteStrengthCertificateType,
  ConcretingRequestMessageType,
  ConcretingRequestType,
  ConcretingType,
  ConstructionEmailType,
  ConstructionType,
  ContractConcreteBatchingPlantType,
  ContractConcreteRecipeType,
  ContractSellerType,
  ContractType,
  DriverType,
  ECommandResultStatus,
  FiscalDocumentType,
  InvoiceInstallmentType,
  InvoiceType,
  ModuleType,
  ParamType,
  PermissionUserType,
  SellerType,
  TravelType,
  UserType,
  VehiclePositionType
} from "./types.graphql";

// Utils Types
type TNonEmptyArray<T> = [T, ...T[]];
type TJoin<K, P> = K extends string | number
  ? P extends string | number
    ? `${K}${"" extends P ? "" : "."}${P}`
    : never
  : never;
type TModeForm = "UPDATE" | "CREATE";

// Entity Types (keyof) - OMIT object / array properties
type TArchiveType = keyof Omit<ArchiveType, "__typename">;
type TClientType = keyof Omit<ClientType, "__typename" | "constructions">;
type TConcretingType = keyof Omit<ConcretingType,
  "contractConcreteRecipe" | "contract" | "travels" | "__typename">;
type TConcretingRequestType = keyof Omit<ConcretingRequestType,
  | "__typename"
  | "contract"
  | "contractConcreteRecipe"
  | "concreteBatchingPlant"
  | "messages">;
type TConcretingRequestMessageType = keyof Omit<ConcretingRequestMessageType,
  "__typename" | "concretingRequest" | "user">;
type TConcreteBatchingPlantType = keyof Omit<ConcreteBatchingPlantType,
  "__typename">;
type TConcreteStrengthCertificateType = keyof Omit<ConcreteStrengthCertificateType,
  "archive" | "concreting" | "__typename">;
type TConcreteMixerType = keyof Omit<ConcreteMixerType, "__typename">;
type TContractType = keyof Omit<ContractType,
  "construction" | "contractConcreteRecipes" | "__typename">;
type TContractConcreteRecipeType = keyof Omit<ContractConcreteRecipeType,
  "concreteRecipe" | "contract" | "__typename">;
type TContractConcreteBatchingPlantType = keyof Omit<ContractConcreteBatchingPlantType,
  "contract" | "concreteBatchingPlant" | "__typename">;
type TConstructionType = keyof Omit<ConstructionType,
  "contracts" | "__typename" | "permissionsUsers">;
type TConstructionEmailType = keyof Omit<ConstructionEmailType,
  "__typename" | "construction">;
type TContractSellerType = keyof Omit<ContractSellerType,
  "seller" | "__typename">;
type TDriverType = keyof Omit<DriverType, "__typename">;
type TInvoiceType = keyof Omit<InvoiceType,
  | "invoiceInstallments"
  | "contract"
  | "fiscalDocument"
  | "archive"
  | "__typename">;
type TInvoiceInstallmentType = keyof Omit<InvoiceInstallmentType, "__typename">;
type TFiscalDocumentType = keyof Omit<FiscalDocumentType,
  "archive" | "__typename">;
type TSellerType = keyof Omit<SellerType, "__typename">;
type TConcreteRecipeType = keyof Omit<ConcreteRecipeType, "__typename">;
type TTravelType = keyof Omit<TravelType,
  "concreteMixer" | "driver" | "concreting" | "__typename">;
type TUserType = keyof Omit<UserType, "__typename" | "permissions">;

type TPermissionUserType = keyof Omit<PermissionUserType,
  "__typename" | "user" | "construction" | "contract" | "module">;
type TModuleType = keyof Omit<ModuleType,
  "__typename" | "permissionsUsers" | "sellerModules">;
type TVehiclePositionType = keyof Omit<VehiclePositionType, "__typename">;
type TParamType = keyof Omit<ParamType, "__typename">;

class Utils {

  /**
   * Build a GQL query string from a GraphQL query array
   * @param {string} args - The GraphQL query array
   */
  buildQuery = (...args) => {
    const set = (a, o = {}) => {
      const k = a.shift();

      o[k] = a.length ? set(a, o[k]) : null;

      return o;
    };

    const query = args.reduce((o, a) => set(a.split("."), o), {});

    return JSON.stringify(query)
      .replace(/"|:|null/g, "")
      .replace(/^\{/, "")
      .replace(/\}$/, "");
  };

  /**
   * Format date and hour
   * @param date Date or Hour or Interval
   * @param type Type of first parameter
   */
  formatDateIfHave = (
    date: Duration | Date | string,
    type:
      | "fullDate"
      | "time"
      | "date"
      | "dateDescriptive"
      | "durationTime"
      | "durationDescriptiveTime"
      | "durationIntervalTime"
      | "year"
      | "fullDateWithoutYear",
    options?: {
      durationDescriptiveTime?: {
        onlyMinutes?: boolean;
      };
    }
  ): string | Date => {
    let intervalDate;

    if (!date) return "";
    if (_.isObject(date)) {
      intervalDate = date as Duration;
    } else {
      intervalDate = date as Date | string;
    }

    switch (type) {
      case "fullDate":
        return dateFns.format(new Date(intervalDate), "dd/MM/yy HH:mm:ss");
      case "fullDateWithoutYear":
        return dateFns.format(new Date(intervalDate), "dd/MM HH:mm:ss");
      case "time":
        return dateFns.format(new Date(intervalDate), "HH:mm:ss");
      case "date":
        return dateFns.format(new Date(intervalDate), "dd/MM/yyyy");
      case "year":
        return dateFns.format(new Date(intervalDate), "yyyy");
      case "durationDescriptiveTime": {
        let formattedTime = "";
        const duration = date as Duration;
        const hours = duration.hours || 0;
        const minutes = duration.minutes || 0;
        const seconds = duration.seconds || 0;

        if (hours > 0) formattedTime += `${hours}h `;
        if (minutes > 0) formattedTime += `${minutes}m `;
        if (hours === 0 && minutes === 0) {
          if (options && options[type]?.onlyMinutes) {
            formattedTime = "agora mesmo";
          } else {
            formattedTime += `${seconds}s`;
          }
        }

        return formattedTime;
      }
      case "durationIntervalTime":
        // eslint-disable-next-line no-case-declarations
        const initialDate = {
          hours: "00",
          minutes: "00",
          seconds: "00"
        };

        Object.keys(intervalDate)
          .forEach(
            (key) => (initialDate[key] = intervalDate[key].toString()
              .padStart(2, "0"))
          );

        return `${initialDate.hours}:${initialDate.minutes}:${initialDate.seconds}`;

      default:
        return "";
    }
  };

  /**
   * Format string currency value to number
   * @param value Currency value
   */
  formatMaskStringToNumber = (value: string): number => {
    // 1. Replace all dots (.) with commas (,)
    // 2. Replace last comma (,) with dot (.)
    // 3. Replace all commas (,) with empty
    const number = Number(
      value
        .replace(/\./g, ",")
        .replace(/,([^,]*)$/, ".$1")
        .replace(/[^\d.]/g, "")
    );

    return number;
  };

  /**
   * Remove special characters from a string
   * @param str The string to clean
   * @returns The cleaned string
   */
  removeSpecialChars = (str: string) => str.replace(/[^a-zA-Z0-9]/g, "");

  /**
   * Simulate click action on a DOM element (First occurrence)
   * @param buttonClasse The class of the button
   */
  clickButtonDomElement = (buttonClass: string) => {
    const button = document.getElementsByClassName(
      buttonClass
    )[0] as HTMLButtonElement;

    if (button) button.click();
  };

  /**
   * Convert command status number to string
   * @param statusNumber The number of status
   */
  commandStatusToString = (statusNumber) => {
    switch (statusNumber) {
      case 0:
        return ECommandResultStatus.SUCCESS;
      case 1:
        return ECommandResultStatus.ERROR;
      default:
        return ECommandResultStatus.ALERT;
    }
  };
}

export type TEnumerableCommandResult =
  | "EnumerableCommandResult_Client"
  | "EnumerableCommandResult_PermissionUser";

export type TCommandResult =
  | "CommandResult_Void"
  | "CommandResult_Archive"
  | "CommandResult_Client"
  | "CommandResult_ConcreteBatchingPlant"
  | "CommandResult_ConcreteStrengthCertificate"
  | "CommandResult_ConcreteMixer"
  | "CommandResult_Contract"
  | "CommandResult_Concreting"
  | "CommandResult_ConcretingRequest"
  | "CommandResult_ConcretingRequestMessage"
  | "CommandResult_ContractConcreteRecipe"
  | "CommandResult_ContractSeller"
  | "CommandResult_Construction"
  | "CommandResult_ConstructionEmail"
  | "CommandResult_Driver"
  | "CommandResult_Invoice"
  | "CommandResult_InvoiceInstallment"
  | "CommandResult_FiscalDocument"
  | "CommandResult_Seller"
  | "CommandResult_ConcreteRecipe"
  | "CommandResult_Travel"
  | "CommandResult_User"
  | "CommandResult_PermissionUser"
  | "CommandResult_Module"
  | "CommandResult_VehiclePosition"
  | "CommandResult_ContractConcreteBatchingPlant"
  | "CommandResult_Param";

export type TListCommandResult =
  | "ListCommandResult_Archive"
  | "ListCommandResult_Client"
  | "ListCommandResult_ConcreteBatchingPlant"
  | "ListCommandResult_ConcreteStrengthCertificate"
  | "ListCommandResult_ConcreteMixer"
  | "ListCommandResult_Contract"
  | "ListCommandResult_Concreting"
  | "ListCommandResult_ConcretingRequest"
  | "ListCommandResult_ConcretingRequestMessage"
  | "ListCommandResult_ContractConcreteRecipe"
  | "ListCommandResult_ContractSeller"
  | "ListCommandResult_Construction"
  | "ListCommandResult_ConstructionEmail"
  | "ListCommandResult_Driver"
  | "ListCommandResult_Invoice"
  | "ListCommandResult_InvoiceInstallment"
  | "ListCommandResult_FiscalDocument"
  | "ListCommandResult_Seller"
  | "ListCommandResult_ConcreteRecipe"
  | "ListCommandResult_Travel"
  | "ListCommandResult_User"
  | "ListCommandResult_PermissionUser"
  | "ListCommandResult_Module"
  | "ListCommandResult_ContractConcreteBatchingPlant"
  | "ListCommandResult_Param";

export default new Utils();
export type {
  TNonEmptyArray,
  TJoin,
  TArchiveType,
  TClientType,
  TConcretingType,
  TConcretingRequestType,
  TConcretingRequestMessageType,
  TConcreteBatchingPlantType,
  TConcreteStrengthCertificateType,
  TConcreteMixerType,
  TContractType,
  TContractConcreteRecipeType,
  TContractSellerType,
  TConstructionType,
  TDriverType,
  TInvoiceType,
  TInvoiceInstallmentType,
  TFiscalDocumentType,
  TSellerType,
  TConcreteRecipeType,
  TTravelType,
  TUserType,
  TModeForm,
  TPermissionUserType,
  TModuleType,
  TConstructionEmailType,
  TVehiclePositionType,
  TContractConcreteBatchingPlantType,
  TParamType
};
